import React, { useState, useEffect, useMemo } from 'react';
import { LogoutOutlined, RightCircleOutlined, LeftCircleOutlined } from "@ant-design/icons";
import { Layout, Menu, Avatar } from "antd";
import { RBHBox, RBHImage, RBHText } from "../../../../shared/components";
import { Colors, Images } from '../../../../shared/theme';

import './styles.css'

import MerchantIcon from '../../../../shared/resources/image/svg/navigation/merchant-icon.svg';
import MenuIcon from '../../../../shared/resources/image/svg/navigation/menu-icon.svg';
import ReportIcon from '../../../../shared/resources/image/svg/navigation/report-icon.svg';
import SettingIcon from '../../../../shared/resources/image/svg/navigation/setting-icon.svg';
import ProfileIcon from '../../../../shared/resources/image/svg/navigation/profile-icon.svg';
import HomeIcon from '../../../../shared/resources/image/svg/navigation/home-icon.svg';

import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AUTH_STATE_KEY, authStateSelector } from '../../../../auth/auth-reducer';
import { textStyles } from '../../../../shared/theme/fonts-ppv';
import { authCreators } from '../../../../auth/auth-action';
import { getLanguageOptions, Locale } from '../../../../language';
import { generalCreators } from '../../../../redux/general/general-action';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../../redux/general/general-reducer';

const SideMenuBar = ({ children }: { children: React.ReactNode }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();

    const email = useSelector(authStateSelector(AUTH_STATE_KEY.EMAIL));
    const currentLocale = useSelector(generalStateSelector(GENERAL_STATE_KEY.LOCALE)) as Locale;
    const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG));
    const showCustomReport = useMemo(() => {
        if (Array.isArray(appConfig)) {
            return appConfig.find((item: any) => item.key === 'feature.custom_report')?.value === 'true';
        }
        return false;
    }, [appConfig]);
    const [collapsed, setCollapsed] = useState(false);

    // Handle responsive collapse based on screen size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setCollapsed(true);
            } else if (window.innerWidth >= 1024) {
                setCollapsed(false);
            }
        };

        // Set initial state
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    
    function onChangeLanguage(locale: Locale) {
        dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.LOCALE, locale));
    }

    const getSelectedKey = () => {
        const path = location.pathname;
        if (path.includes('/branchmanagement')) return 'shop-list';
        if (path.includes('/usermanagement')) return 'shop-managers';
        if (path.includes('/vataddress')) return 'tax-invoice-address';
        if (path.includes('/foodcategory')) return 'category-menu';
        if (path.includes('/modifier-group')) return 'modifier-groups';
        if (path.includes('/modifier-item')) return 'modifier-items';
        if (path.includes('/custom-report')) return 'custom-report';
        if (path.includes('/report')) return 'sales-report';
        if (path === '/main') return 'main';
        return '';
    };


    return (
        <Layout style={{ height: '100vh', overflow: 'hidden' }}>
         <Layout.Sider
            width={220}
            collapsible
            collapsed={collapsed}
            trigger={
                <div style={{ backgroundColor: 'white', borderRight: `1px solid ${Colors.grey100}` }}>
                    {
                        collapsed 
                        ? <RightCircleOutlined style={{ fontSize: '30px', color: Colors.violet600 }} translate={'none'} />
                        : <LeftCircleOutlined style={{ fontSize: '30px', color: Colors.violet600 }} translate={'none'} />
                    }
                    
                </div>
            }
            collapsedWidth={80}
            breakpoint="lg"
            onBreakpoint={(broken) => setCollapsed(broken)}
            onCollapse={(col) => setCollapsed(col)}
            style={{
                backgroundColor: Colors.white,
                height: '100vh',
                position: 'sticky',
                top: 0,
                left: 0,
            }}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                {/* Header with Logo and Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    height: '64px',
                    position: 'relative',
                    borderRight: `1px solid ${Colors.grey100}`
                }}>
                    {
                        collapsed ? 
                            <RBHImage
                                style={{ height: '40px', width: 'auto', maxWidth: '48px', objectFit: 'contain' }}
                                src={Images.logoIcon}
                            />
                        :  
                            <RBHImage
                                style={{ width: '140px', height: '50px' }}
                                src={Images.logoIcon2}
                            />
                    }
                </div>

                {/* Menu Items */}
                <Menu
                    mode="inline"
                    selectedKeys={[getSelectedKey()]}
                    inlineCollapsed={collapsed}
                    style={{ flex: 1, borderRight: 0, overflowY: 'auto', overflowX: 'hidden' }}
                >
                    <Menu.Item
                        key='main'
                        onClick={() => history.push('/main')}
                        icon={<RBHImage src={HomeIcon} style={{ width: 24, height: 24 }} />}
                    >
                        {!collapsed ? <FormattedMessage id={'main.menu-item.start'} /> : ''}
                    </Menu.Item>
                    <Menu.SubMenu
                        icon={<RBHImage src={MerchantIcon} style={{ width: 24, height: 24 }} />}
                        title={!collapsed ? <FormattedMessage id={'main.menu-item.merchant'} /> : ''}
                    >
                        <Menu.Item onClick={() => history.push('/main/branchmanagement')} key="shop-list"><FormattedMessage id={'main.menu-item.merchant.branch'} /></Menu.Item>
                        <Menu.Item onClick={() => history.push('/main/usermanagement')} key="shop-managers"><FormattedMessage id={'main.menu-item.merchant.branch-manager'} /></Menu.Item>
                        <Menu.Item onClick={() => history.push('/main/vataddress')} key="tax-invoice-address"><FormattedMessage id={'main.menu-item.merchant.vataddress'} /></Menu.Item>
                    </Menu.SubMenu>
                    <Menu.SubMenu
                        icon={<RBHImage src={MenuIcon} style={{ width: 24, height: 24 }} />}
                        title={!collapsed ? <FormattedMessage id={'main.menu-item.menu'} /> : ''}
                    >
                        <Menu.Item onClick={() => history.push('/main/foodcategory')} key="category-menu"><FormattedMessage id={'main.menu-item.menu.category'} /></Menu.Item>
                        <Menu.Item onClick={() => history.push('/main/modifier-group')} key="modifier-groups"><FormattedMessage id={'main.menu-item.menu.modifier-group'} /></Menu.Item>
                        <Menu.Item onClick={() => history.push('/main/modifier-item')} key="modifier-items"><FormattedMessage id={'main.menu-item.menu.modifier-item'} /></Menu.Item>
                    </Menu.SubMenu>
                    <Menu.SubMenu
                        icon={<RBHImage src={ReportIcon} style={{ width: 24, height: 24 }} />}
                        title={!collapsed ? <FormattedMessage id={'main.menu-item.report'} /> : ''}
                    >
                        <Menu.Item onClick={() => history.push('/main/report')} key="sales-report"><FormattedMessage id={'main.menu-item.report.sales'} /></Menu.Item>
                        {showCustomReport && <Menu.Item onClick={() => history.push('/main/custom-report')} key="custom-report"><FormattedMessage id={'main.menu-item.report.custom'} /></Menu.Item>}
                    </Menu.SubMenu>
                     <Menu.Item
                        onClick={() => history.push('/main/devicemanagement')}
                        icon={<RBHImage src={SettingIcon} style={{ width: 24, height: 24 }} />}
                    >
                        {!collapsed ? <FormattedMessage id={'main.menu-item.manage-devices'} /> : ''}
                    </Menu.Item>
            </Menu>

            {/* Account Section Footer */}
            <Menu mode='vertical' selectable={false} className="sidebar-account-menu">
                <Menu.SubMenu
                    title={
                        <RBHBox
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: 8
                            }}
                        >
                            <div style={{ 
                                width: 32, 
                                height: 32, 
                                borderRadius: '100%', 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center', 
                                overflow: 'clip' 
                            }}>
                                <img src={ProfileIcon} style={{ margin: 0 }} />
                            </div>
                            
                            {!collapsed && (
                                <RBHText
                                    style={{
                                        ...textStyles.subtitle2,
                                        color: Colors.grey600,
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        maxWidth: '120px'
                                    }}
                                >
                                    {email}
                                </RBHText>
                            )}
                        </RBHBox>
                    }>
                    <Menu.SubMenu 
                    title={
                        <RBHBox style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {!collapsed && <RBHText style={{ ...textStyles.subtitle2, color: Colors.grey600 }} text="Language" />}
                            <RBHText style={{ ...textStyles.h4 }}>{currentLocale.toUpperCase()}</RBHText>
                        </RBHBox>
                    }>
                    {getLanguageOptions().map((option) => {
                        return (
                            <Menu.Item 
                                key={option.value}  
                                style={{
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    alignItems: 'center',
                                }}>
                                <a id={`${option.value}-translation-menu`} onClick={() => onChangeLanguage(option.value)}>
                                    <RBHText
                                        id={`${option.value}-translation-menu-text`}
                                        data-testid={`${option.value}-translation-menu-text`}
                                        style={{ ...textStyles.subtitle2 }}
                                    >
                                        {option.label.toUpperCase()}
                                    </RBHText>
                                </a>
                            </Menu.Item>
                        );
                    })}
                </Menu.SubMenu>
                    <Menu.Item
                        style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}
                        key="logout"
                        icon={<LogoutOutlined style={{ fontSize: 16, color: Colors.grey600 }} translate={'none'} />}
                        onClick={() => dispatch(authCreators.logOut())}
                    >
                        <RBHText style={{ ...textStyles.subtitle2, color: Colors.grey600 }} text={'main.logout'} />
                    </Menu.Item>
                </Menu.SubMenu>
            </Menu>
        </div>
        </Layout.Sider>
        <Layout.Content
            style={{
                padding: '24px',
                backgroundColor: Colors.grey50,
                overflow: 'auto',
                width: '100%',
            }}>
            {children}
        </Layout.Content>
      </Layout>
    )
}

export default SideMenuBar;